<script setup>
import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { fetchDatasetById } from '@/services/api';
import Stepper from '@/components/Stepper.vue';
import EvalBuilderView from '@/pages/eval/eval-builder/EvalBuilderView.vue';
import EvalBuilderAgenticView from '@/pages/eval/eval-builder/EvalBuilderAgenticView.vue';
import { EVAL_TYPES, isValidEvalType } from '@/pages/eval/eval-builder/constants/evalTypes';

const route = useRoute();
const router = useRouter();

const resolvedEvalType = ref(null);
const selectedEvalType = ref(EVAL_TYPES.AGENTIC);
const isResolvingDatasetType = ref(Boolean(route.query.datasetId));

const steps = ['Choose Eval Type', 'Create Eval', 'Listing Details'];
const currentStep = 1;

const datasetId = computed(() => {
  const value = route.query.datasetId;
  if (!value || typeof value !== 'string') return null;
  return value.trim() ? value : null;
});

const isEditing = computed(() => Boolean(datasetId.value));

const normalizeEvalType = (value) => {
  if (!value || typeof value !== 'string') return null;
  return isValidEvalType(value) ? value : null;
};

const evalTypeDetails = {
  [EVAL_TYPES.AGENTIC]: {
    title: 'Agentic, Multi-Turn',
    subtitle: 'Task-driven evals with configurable tools and iterative execution.',
    bestFor: 'Best for realistic tasks where models need tooling, memory, and longer horizons (for example, TerminalBench-style tasks).',
    bullets: [
      'Model can run in a loop and use tools you configure.',
      'Closer to real production behavior and harder tasks.',
      'Requires deeper auditing of tool behavior and traces.',
    ],
    gradingSummary: 'Rubric criteria are judged by an LLM-as-a-judge jury (ensemble). Agentic supports semantic plus lexical checks (for example, exact match).',
    cta: 'Create Agentic Eval',
    cardTitle: 'Agentic, Multi-Turn',
    cardDescription: 'Best for realistic workflows with configurable tool use.',
    iconClass: 'uil uil-robot',
  },
  [EVAL_TYPES.QA]: {
    title: 'Q&A, Single-Turn',
    subtitle: 'Prompt-driven evals focused on direct model reasoning.',
    bestFor: 'Best for cleaner benchmarking and easier auditing of direct model answers (for example, Humanity\'s Last Exam (HLE)-style questions).',
    bullets: [
      'One prompt in, one answer out.',
      'No external tool loop.',
      'Easier to compare models on core reasoning.',
    ],
    gradingSummary: 'Rubric criteria are judged by an LLM-as-a-judge jury (ensemble). Q&A currently supports semantic grading only.',
    cta: 'Create Q&A Eval',
    cardTitle: 'Q&A, Single-Turn',
    cardDescription: 'Direct model reasoning with no external tool loop.',
    iconClass: 'uil uil-comment-question',
  },
};

const selectedEvalTypeDetails = computed(() => evalTypeDetails[selectedEvalType.value] || evalTypeDetails[EVAL_TYPES.QA]);

const resolveEvalType = async () => {
  if (isEditing.value) {
    isResolvingDatasetType.value = true;
    try {
      const dataset = await fetchDatasetById(datasetId.value);
      const datasetType = normalizeEvalType(dataset?.eval_type || dataset?.eval?.eval_type);
      resolvedEvalType.value = datasetType || EVAL_TYPES.QA;
      selectedEvalType.value = resolvedEvalType.value;
    } catch (error) {
      console.error('Error resolving eval type for edit flow:', error);
      resolvedEvalType.value = EVAL_TYPES.QA;
      selectedEvalType.value = EVAL_TYPES.QA;
    } finally {
      isResolvingDatasetType.value = false;
    }
    return;
  }

  const queryType = normalizeEvalType(route.query.evalType);
  if (queryType) {
    selectedEvalType.value = queryType;
  }

  isResolvingDatasetType.value = false;
  resolvedEvalType.value = queryType;
};

watch(
  () => [route.query.datasetId, route.query.evalType],
  () => {
    resolveEvalType();
  },
  { immediate: true }
);

const selectEvalType = async (type) => {
  if (!isValidEvalType(type)) return;
  await router.replace({
    path: route.path,
    query: {
      ...route.query,
      evalType: type,
      subpage: route.query.subpage || 'eval-builder',
    },
  });
};

const proceedToCreateEval = async () => {
  await selectEvalType(selectedEvalType.value);
};

const handleSelectEvalType = (type) => {
  if (!isValidEvalType(type)) return;
  selectedEvalType.value = type;
};
</script>

<template>
  <div v-if="isResolvingDatasetType" class="h-full w-full flex items-center justify-center">
    <div class="loading-spinner"></div>
  </div>

  <div v-else-if="!isEditing && !resolvedEvalType" class="creator-profile__container h-full overflow-hidden">
    <div class="flex sm:flex-col items-center justify-between">
      <div class="breadcrumb w-full">
        <router-link to="/creator-profile?page=eval-datasets&subpage=eval-datasets" class="breadcrumb-link">
          <svg class="breadcrumb-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
            <path
              fill-rule="evenodd"
              d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
              clip-rule="evenodd"
            />
          </svg>
          <span class="whitespace-nowrap text-sm">Back to Evals</span>
        </router-link>
      </div>
      <div class="creator-profile__container-stepper">
        <Stepper :steps="steps" :currentStep="currentStep" />
      </div>
    </div>

    <div class="creator-profile__container-content w-full flex flex-col lg:flex-row items-stretch lg:h-[calc(100vh-200px)] min-h-0 pb-4 lg:pb-0">
      <div class="w-full lg:w-[40%] lg:pr-10 pb-3 overflow-y-auto overflow-x-hidden gradient-divider min-h-0">
        <div class="w-full flex flex-col items-center justify-center mb-8">
          <h1 class="creator-profile__container-content-title">Choose Eval Type</h1>
          <p class="text-sm 2xl:text-base text-gray-300 text-center">
            Pick the mode that best matches your evaluation goal.
          </p>
        </div>

        <div class="grid grid-cols-1 gap-4">
          <button
            class="dataset-type-card"
            :class="selectedEvalType === EVAL_TYPES.AGENTIC ? 'active' : 'inactive'"
            @click="handleSelectEvalType(EVAL_TYPES.AGENTIC)"
            data-testid="eval-type-agentic-card"
          >
            <i :class="[evalTypeDetails[EVAL_TYPES.AGENTIC].iconClass, 'dataset-type-card-icon', selectedEvalType === EVAL_TYPES.AGENTIC ? 'active' : '']"></i>
            <div>
              <div class="dataset-type-card-title">{{ evalTypeDetails[EVAL_TYPES.AGENTIC].cardTitle }}</div>
              <div class="dataset-type-card-description">{{ evalTypeDetails[EVAL_TYPES.AGENTIC].cardDescription }}</div>
            </div>
          </button>

          <button
            class="dataset-type-card"
            :class="selectedEvalType === EVAL_TYPES.QA ? 'active qa-active' : 'inactive'"
            @click="handleSelectEvalType(EVAL_TYPES.QA)"
            data-testid="eval-type-qa-card"
          >
            <i :class="[evalTypeDetails[EVAL_TYPES.QA].iconClass, 'dataset-type-card-icon', selectedEvalType === EVAL_TYPES.QA ? 'active qa-active' : '']"></i>
            <div>
              <div class="dataset-type-card-title">{{ evalTypeDetails[EVAL_TYPES.QA].cardTitle }}</div>
              <div class="dataset-type-card-description">{{ evalTypeDetails[EVAL_TYPES.QA].cardDescription }}</div>
            </div>
          </button>
        </div>
      </div>

      <div class="w-full lg:w-1/2 lg:pl-10 pr-3 pb-3 overflow-y-auto min-h-0">
        <div class="form-container h-full">
          <div class="w-full flex flex-col items-center justify-center mb-8">
            <h1 class="creator-profile__container-content-title">{{ selectedEvalTypeDetails.title }}</h1>
            <p class="text-sm 2xl:text-base text-gray-300 text-center mt-1">
              {{ selectedEvalTypeDetails.subtitle }}
            </p>
          </div>

          <div class="form-fields justify-between h-full">
            <div class="space-y-5">
              <div class="detail-card">
                <h3 class="detail-card-title">Best For</h3>
                <p class="detail-card-text">{{ selectedEvalTypeDetails.bestFor }}</p>
              </div>

              <div class="detail-card">
                <h3 class="detail-card-title">What This Means</h3>
                <ul class="list-disc pl-5 space-y-2 text-sm 2xl:text-base text-white/85">
                  <li v-for="bullet in selectedEvalTypeDetails.bullets" :key="bullet">
                    {{ bullet }}
                  </li>
                </ul>
              </div>

              <div class="detail-card">
                <h3 class="detail-card-title">Grading</h3>
                <p class="detail-card-text">{{ selectedEvalTypeDetails.gradingSummary }}</p>
              </div>

              <div v-if="selectedEvalType === EVAL_TYPES.AGENTIC" class="text-sm text-white/60">
                Harbor docs:
                <a
                  href="https://harborframework.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-echoes-primary hover:text-echoes-primary/80 underline"
                >
                  harborframework.com
                </a>
              </div>
            </div>

            <div class="action-container">
              <button class="create-eval-btn" @click="proceedToCreateEval" data-testid="create-eval-from-selector">
                {{ selectedEvalTypeDetails.cta }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div v-else class="h-full">
    <EvalBuilderAgenticView
      v-if="resolvedEvalType === EVAL_TYPES.AGENTIC"
      :key="`agentic-${datasetId || 'new'}`"
    />
    <EvalBuilderView
      v-else
      :evalType="EVAL_TYPES.QA"
      :key="`qa-${datasetId || 'new'}`"
    />
  </div>
</template>

<style scoped lang="postcss">
.creator-profile__container {
  @apply relative max-w-7xl mx-auto h-full;
}

.creator-profile__container-stepper {
  @apply flex flex-col items-end sm:items-center justify-center mt-5 mb-10;
}

.creator-profile__container-content {
  @apply h-full flex justify-center;
}

.creator-profile__container-content-title {
  @apply text-xl sm:text-2xl xl:text-3xl font-bold bg-gradient-to-r from-[#FFFFFF] to-[#999999] bg-clip-text text-transparent w-fit;
}

.form-container {
  @apply flex flex-col w-full;
}

.form-fields {
  @apply space-y-6 flex flex-col;
}

.dataset-type-card {
  @apply flex items-center rounded-2xl border py-5 2xl:py-6 px-3 bg-echoes-background-gray/50 cursor-pointer transition-all duration-300 gap-3 text-left;
}

.dataset-type-card:hover {
  @apply bg-echoes-background-gray/70;
}

.dataset-type-card.inactive {
  @apply border-echoes-border-gray;
}

.dataset-type-card.active {
  @apply border-echoes-primary;
}

.dataset-type-card.active.qa-active {
  border-color: rgb(74 222 128 / 0.8);
}

.dataset-type-card-icon {
  @apply text-4xl flex items-center justify-center w-10 flex-shrink-0 text-white/70;
}

.dataset-type-card-icon.active {
  @apply text-echoes-primary;
}

.dataset-type-card-icon.active.qa-active {
  color: rgb(134 239 172 / 1);
}

.dataset-type-card-title {
  @apply text-base 2xl:text-lg font-medium text-white;
}

.dataset-type-card-description {
  @apply text-sm text-gray-400;
}

.detail-card {
  @apply rounded-xl border border-echoes-border-gray bg-echoes-background-black/60 p-4;
}

.detail-card-title {
  @apply text-sm uppercase tracking-wide text-white/70 mb-2 font-semibold;
}

.detail-card-text {
  @apply text-sm 2xl:text-base text-white/90;
}

.action-container {
  @apply flex justify-end mt-8;
}

.create-eval-btn {
  @apply px-6 2xl:px-8 py-2 my-6 2xl:text-base text-sm bg-gradient-to-br from-green-500 to-green-700 text-white font-semibold rounded-full hover:bg-gradient-to-br hover:from-green-600 hover:to-green-800 transition duration-200 flex items-center justify-center min-w-[180px];
}

.breadcrumb {
  @apply w-fit mt-4;
}

.breadcrumb-link {
  @apply flex underline items-center gap-1 text-white/80 hover:text-echoes-primary transition-colors duration-200 text-sm 2xl:text-base;
}

.breadcrumb-icon {
  @apply w-4 h-4 -ml-1;
}

.gradient-divider {
  position: relative;
}

.gradient-divider::after {
  content: '';
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 2px;
  background: linear-gradient(to top, transparent 0%, #232323 100%);
}

@media (max-width: 1023px) {
  .gradient-divider::after {
    display: none;
  }
}

@media (max-height: 860px) {
  .creator-profile__container-stepper {
    @apply mb-6;
  }

  .form-fields {
    @apply space-y-4;
  }

  .dataset-type-card {
    @apply py-4;
  }

  .detail-card {
    @apply p-3;
  }

  .action-container {
    @apply mt-4;
  }

  .create-eval-btn {
    @apply my-3;
  }
}

.loading-spinner {
  width: 2rem;
  height: 2rem;
  border: 4px solid rgba(43, 45, 51, 1);
  border-top-color: #FE3E01;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
